import Shepherd from 'shepherd.js'
import {postJson} from '../../../../resources/js/product-tours'
import {buildTourStepElement, NURSE_PERFORMANCE_TOUR_STEPS} from './NursePerformanceTourContent'
import {claimTourStart, hasVisibleModal, releaseTourStart, requestedTourFromHash} from './TourStartGuard'
import {recordNursePerformanceInteraction} from './nurse-performance-interactions'

const PANEL_SELECTOR = '[data-tour="nurse-performance-panel"]'
const INITIALIZED_ATTRIBUTE = 'data-nurse-performance-tour-initialized'
// Matches the `#cpm-tour=<slug>` convention read by resources/js/product-tours.js, so the Tours &
// Guides drawer entry (config/tours.php: nurse_my_performance_progress) can launch this tour.
const CPM_TOUR_EVENT = 'cpm:tour:nurse_my_performance_progress'
const CPM_TOUR_ID = 'nurse_my_performance_progress'

let activeTour = null
const tourStartState = {pending: false}

function tourButton(action, text, classes = '') {
    return {
        action,
        classes,
        text,
    }
}

function recordAcknowledgement(panel, status) {
    if (panel.dataset.tourPersistent !== 'true') {
        return
    }

    const component = panel.closest('[wire\\:id]')
    const componentId = component?.getAttribute('wire:id')

    if (!componentId || !window.Livewire) {
        return
    }

    window.Livewire.find(componentId)?.call('acknowledgePerformanceTour', status)
}

function buildTour(panel) {
    const tour = new Shepherd.Tour({
        defaultStepOptions: {
            cancelIcon: {enabled: true},
            classes: 'nurse-performance-tour',
            modalOverlayOpeningPadding: 10,
            modalOverlayOpeningRadius: 18,
            scrollTo: {behavior: 'smooth', block: 'center'},
        },
        exitOnEsc: true,
        exitOnOverlayClick: true,
        useModalOverlay: true,
    })

    const steps = NURSE_PERFORMANCE_TOUR_STEPS.filter(
        (step) => panel.querySelector(step.selector) || document.querySelector(step.selector),
    )

    steps.forEach((step, index) => {
        const isFirst = index === 0
        const isLast = index === steps.length - 1
        const buttons = []

        if (isFirst) {
            buttons.push(tourButton(() => tour.cancel(), 'Not now', 'shepherd-button-secondary'))
        } else {
            buttons.push(tourButton(() => tour.back(), 'Back', 'shepherd-button-secondary'))
        }

        buttons.push(tourButton(
            () => isLast ? tour.complete() : tour.next(),
            isLast ? 'Done' : (isFirst ? 'Start tour' : 'Next'),
        ))

        tour.addStep({
            id: `nurse-performance-${index + 1}`,
            attachTo: {
                element: step.selector,
                on: step.position,
            },
            buttons,
            text: buildTourStepElement(step, index, steps.length),
            title: step.title,
        })
    })

    let acknowledgementRecorded = false
    const acknowledge = (status) => {
        if (acknowledgementRecorded) {
            return
        }

        acknowledgementRecorded = true
        recordAcknowledgement(panel, status)
        recordNursePerformanceInteraction(
            panel,
            status === 'completed' ? 'tour_completed' : 'tour_dismissed',
            'tour',
        )
        postJson(window, `/tours/${CPM_TOUR_ID}/${status === 'completed' ? 'complete' : 'dismiss'}`)
        activeTour = null
    }

    tour.on('complete', () => acknowledge('completed'))
    tour.on('cancel', () => acknowledge('dismissed'))

    return tour
}

function expandPanel(panel) {
    const disclosure = panel.querySelector('[data-nurse-performance-disclosure]')

    if (disclosure instanceof HTMLInputElement) {
        disclosure.checked = true

        return
    }

    const toggle = panel.querySelector('[data-cy="nurse-performance-toggle"]')

    if (toggle?.getAttribute('aria-expanded') === 'false') {
        toggle.click()
    }
}

function hasCompetingTour() {
    const requestedTour = requestedTourFromHash(window.location.hash)

    return Boolean(Shepherd.activeTour || (requestedTour && requestedTour !== CPM_TOUR_ID))
}

// The coaching panel is a lazy Livewire component: when the `#cpm-tour=` hash is handled on page
// load only its placeholder exists, so the start event is missed. The real panel re-checks the
// hash when it is initialised.
function isRequestedByHash() {
    return requestedTourFromHash(window.location.hash) === CPM_TOUR_ID
}

function startTour(panel) {
    if (hasCompetingTour()) {
        return
    }

    if (!claimTourStart(tourStartState, activeTour)) {
        return
    }

    let modalObserver = null
    let openingDelayElapsed = false
    const attemptStart = () => {
        if (!panel.isConnected) {
            modalObserver?.disconnect()
            releaseTourStart(tourStartState)
            return
        }

        if (hasCompetingTour()) {
            modalObserver?.disconnect()
            releaseTourStart(tourStartState)
            return
        }

        if (hasVisibleModal(document)) {
            if (!modalObserver) {
                modalObserver = new MutationObserver(attemptStart)
                modalObserver.observe(document.documentElement, {
                    attributes: true,
                    attributeFilter: ['class', 'open', 'style'],
                    childList: true,
                    subtree: true,
                })
            }
            return
        }

        modalObserver?.disconnect()
        modalObserver = null

        if (!openingDelayElapsed) {
            openingDelayElapsed = true
            expandPanel(panel)
            window.setTimeout(attemptStart, 350)
            return
        }

        try {
            activeTour = buildTour(panel)
            activeTour.start()
            recordNursePerformanceInteraction(panel, 'tour_started', 'tour')
            postJson(window, `/tours/${CPM_TOUR_ID}/start`)
        } finally {
            releaseTourStart(tourStartState)
        }
    }

    attemptStart()
}

function initializePanel(panel) {
    if (panel.hasAttribute(INITIALIZED_ATTRIBUTE)) {
        return
    }

    panel.setAttribute(INITIALIZED_ATTRIBUTE, 'true')
    panel.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) {
            return
        }

        if (event.target.closest('[data-cy="nurse-performance-toggle"]')) {
            const disclosure = panel.querySelector('[data-nurse-performance-disclosure]')

            if (disclosure instanceof HTMLInputElement) {
                event.preventDefault()
                disclosure.checked = !disclosure.checked
            }
        }

        if (event.target.closest('[data-nurse-performance-tour-start]')) {
            startTour(panel)
        }
    })

    if (panel.dataset.tourAutoStart === 'true' || isRequestedByHash()) {
        window.setTimeout(() => startTour(panel), 600)
    }
}

function initializePanels(root = document) {
    if (root.matches?.(PANEL_SELECTOR)) {
        initializePanel(root)
    }

    root.querySelectorAll?.(PANEL_SELECTOR).forEach(initializePanel)
}

document.addEventListener('DOMContentLoaded', () => initializePanels())
document.addEventListener('livewire:navigated', () => initializePanels())

window.addEventListener(CPM_TOUR_EVENT, () => {
    const panel = document.querySelector(PANEL_SELECTOR)

    if (panel) {
        startTour(panel)
    }
})

new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
                initializePanels(node)

                const panel = node.closest(PANEL_SELECTOR)

                if (panel) {
                    initializePanel(panel)
                }
            }
        })
    })
}).observe(document.documentElement, {childList: true, subtree: true})
